import type { Kit, KitComponent, PartNode } from '@partful/shared-types';
import { makeKitUrl } from '@services/kits/kitUrl';
import type { PartPathWithHash } from '@services/partPath';

/**
 * Converts a Kit object into a PartNode so it can be treated as a saleable part within the basket/orders system
 * @param kit - The kit to convert
 * @returns A PartNode representation of the kit with price information that can be added to basket
 */
export function convertKitToPart(kit: Kit): PartNode {
  // This currently grabs the first price from the kit's prices array. Currently kit ingest doesn't support currency
  // When currency selection is implemented for kit/sbom ingestion this should be revisited
  const price = kit.prices[0]; // TODO: Implement multiple prices/multicurrency
  const por: boolean | undefined = price?.priceOnRequest;
  const amount: number | undefined = price?.amount;
  const kitPartPrice = por === true || amount === undefined ? 'por' : amount;

  return {
    partNumber: kit.externalId,
    price: kitPartPrice,
    name: kit.name,
    id: kit.externalId,
    children: [],
    url: kit.url,
  };
}

/**
 * Flattens a tree structure into a one-dimensional array of nodes.
 *
 * @param {PartNode} bom - The root node of the tree structure to flatten.
 * It should contain child nodes.
 * @return {PartNode[]} An array containing all the nodes in the tree, including the root node, in a flattened structure.
 */
export function flattenTree(bom: PartNode): PartNode[] {
  const flat = (part: PartNode): PartNode[] => [part, ...part.children.flatMap(flat)];
  return bom.children.flatMap(flat).map((part) => ({ ...part, children: [] }));
}

type KitComponentsAndParts = { component: KitComponent; part: PartNode | undefined; inCAD: boolean };

/**
 * This function retrieves kit components from a given kit and matches them with parts from a part tree.
 *
 * @param {Kit} kit - The kit object containing a collection of components.
 * @param {PartNode} bom - The root node of the part tree (Bill of Materials) to be matched with the kit components.
 * @return {Map<string, {component: KitComponent, part: PartNode | undefined}>} A map associating each kit component with its matching part from the part tree, or undefined if no match is found.
 * The map key is the externalId for the kit component
 */
export function getKitComponentsAndMatchingPartsFromPartTree(
  kit: Kit,
  bom?: PartNode
): Map<string, KitComponentsAndParts> {
  if (!bom?.children) {
    throw new Error('Current part tree is undefined');
  }
  const { components } = kit;
  const componentsMap = new Map<string, KitComponentsAndParts>();
  const flatTree = flattenTree(bom);

  components.forEach((component) => {
    const part = flatTree.find((part) => part.partNumber === component.externalId);
    componentsMap.set(component.externalId, {
      component,
      part,
      inCAD: part !== undefined,
    });
  });

  return componentsMap;
}

/**
 * Gets the GlbIDs (mesh names) of kit components that exist in the CAD model
 * @param kitComponents - Array of kit components to check against the CAD model
 * @param bom
 * @returns Array of GLB IDs for components that match parts in the CAD model
 * @throws Error if the part tree (bom) is undefined
 */
export function getKitComponentMeshIds(kitComponents: KitComponent[], bom?: PartNode): string[] {
  if (!bom?.children) {
    throw new Error('Current part tree is undefined');
  }

  const partNumberToGlbIds = getPartNumbersAndGlbIdsFromPartTree(bom);
  return kitComponents
    .filter((component) => partNumberToGlbIds.has(component.externalId))
    .flatMap((component) => partNumberToGlbIds.get(component.externalId) ?? []);
}

/**
 * Recursively traverses a part tree and builds a map of part numbers to their GLB IDs
 * @param {PartNode} bom - The root node of the part tree (Bill of Materials).
 * @param map - Optional map to accumulate results, defaults to new empty Map
 * @returns Map where keys are part numbers and values are arrays of GLB IDs
 */
export const getPartNumbersAndGlbIdsFromPartTree = (bom: PartNode, map: Map<string, string[]> = new Map()) => {
  flattenTree(bom).forEach((part) => map.set(part.partNumber, part.glbIds as string[]));
  return map;
};

/**
 * Converts an array of kits into an array of PartPathWithHash objects
 * Each kit is transformed into a part with basic focal point and empty children
 * If kits array is undefined/null, returns empty array
 * @param kits - Array of Kit objects to convert
 * @returns Array of PartPathWithHash objects representing the kits
 */
export const kitsToPartPaths = (kits: Kit[]): PartPathWithHash[] =>
  kits?.map((kit) => ({
    part: {
      id: kit.externalId,
      name: kit.name,
      children: [],
      partNumber: kit.externalId,
      focal: {
        target: [0, 0, 0],
        position: [0, 0, 0],
      },
      kits: [kit],
    },
    name: kit.name,
    pathHash: kit.externalId,
    path: [],
    parents: [],
    partNumber: kit.externalId,
  })) || [];

/**
 * Interface representing a Kit combined with is navigation url
 * @interface KitWithUrl
 * @property {Kit} kit - The Kit object containing the kit details
 * @property {string} navUrl - The URL path to navigate to this kit's details page
 */
export interface KitWithUrl {
  kit: Kit;
  navUrl: string;
}

/**
 * Finds all kits that contain a specific part and generates URLs for each kit
 * @param part - The part node to search for in kits
 * @param kits - Array of Kit objects to search through
 * @param productId - The ID of the product containing the kits
 * @returns Array of KitWithUrl objects containing the matched kits and their URLs
 */
export const getKitsRelatedToPart = (part: PartNode, kits: Kit[], productId: string): KitWithUrl[] => {
  if (!part || !kits?.length || !productId) {
    return [];
  }

  const partNumberToMatch = part.partNumber;

  return kits
    .filter((kit) => kit.components?.some((component) => component.externalId === partNumberToMatch))
    .map((kit) => ({
      kit,
      navUrl: makeKitUrl({ productId, externalId: kit.externalId }),
    }));
};
